/**
 * AboutPage — the why of Katha Mithra.
 *
 * Copy is mirrored from dev_prompts/about-page-copy.md; that file is the
 * editorial source of truth. When the copy changes, update both — the
 * markdown stays in sync so future copy edits don't require reading JSX.
 *
 * Section structure maps 1:1 to the markdown headings so a writer can
 * locate a paragraph by section number alone. Sanskrit terms follow the
 * "italicize + gloss once" pattern from the markdown — diacritics on
 * first use, plain after.
 */

import { useNavigate } from 'react-router-dom';
import PageHeader, { ToolbarCaptionButton } from '@/components/PageHeader';
import BrandLogo from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import { Feather } from 'lucide-react';
import Seo from '@/components/Seo';

// Bullets for "Search by inner state" — kept as data so the typographic
// treatment (italic, leading dash) is applied uniformly.
const INNER_STATE_PROMPTS = [
  'I am feeling low.',
  'I feel lost.',
  'My body is failing me.',
  'I am struggling with discipline.',
  'I want to do something meaningful, and I don’t know where to start.',
];

// The four story types — kept in `story_type` enum order so the copy
// here cannot drift from the schema (personal | sustainable | wisdom | kala).
const STORY_KINDS = [
  {
    title: 'Personal Transformation Stories',
    status: 'Live now',
    body: 'Real people who faced inner or outer struggles and found a way through.',
    takeaway: 'Someone walked through this. I am not alone. There is a way.',
  },
  {
    title: 'Sustainable Living and Eco Solutions',
    status: null,
    body: 'People quietly healing the world. Soil, water, waste, farming, ecology, community.',
    takeaway: 'People are solving real problems. Maybe I can too.',
  },
  {
    title: 'Wisdom Stories',
    status: null,
    body: 'The Puranas, Upanishads, Itihasas. What our ancestors saw clearly, and what we have collectively forgotten.',
    takeaway: null,
  },
  {
    title: 'Kala and Mastery Stories',
    status: null,
    body: 'The sixty-four kalas. What it looks like when someone gives themselves fully to a craft. Mastery as a path of becoming.',
    takeaway: null,
  },
];

// Small heading used above each major section. Uppercase microtype gives
// the page a rhythm without competing with the serif H2s.
const SectionEyebrow = ({ children }) => (
  <div className="text-xs uppercase tracking-[0.18em] text-[#C05621] mb-3">
    {children}
  </div>
);

const SectionHeading = ({ children }) => (
  <h2 className="text-3xl md:text-4xl font-serif tracking-tight leading-tight text-[#2D2A26] mb-6">
    {children}
  </h2>
);

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="About · Katha Mithra"
        description="Why Katha Mithra exists — a friend of stories. How real, lived accounts of inner transformation rooted in spiritual practice are gathered, tagged, and shared."
        path="/about"
      />
      <PageHeader>
        <ToolbarCaptionButton
          label="Back to Home"
          shortLabel="Home"
          onClick={() => navigate('/')}
          data-testid="about-back-btn"
        />
      </PageHeader>

      {/* ----- 1. Hero -------------------------------------------------- */}
      {/* The logo is the centerpiece here, per the brief: largest mark on
          any page in the app. Cap at ~10rem so it stays balanced on
          desktop without dominating the fold.

          Sequence: mark → wordmark (English + Devanagari) → promise →
          description. This lets the H1 be purely the promise — the
          subtitle no longer has to back-introduce the brand name. */}
      <header
        className="px-4 sm:px-6 lg:px-8 pt-16 pb-12 text-center"
        data-testid="about-hero"
      >
        <BrandLogo
          className="h-32 w-32 sm:h-40 sm:w-40 mx-auto mb-6 drop-shadow-sm"
          alt="Katha Mithra"
        />
        {/* Wordmark — scaled-up version of the PageHeader pairing.
            English serif on top, Devanagari directly below in a lighter
            weight so the two scripts read as one unit, not a translation. */}
        <div className="mb-10" data-testid="about-wordmark">
          <div className="text-2xl sm:text-3xl font-serif font-semibold text-[#2D2A26] tracking-tight">
            Katha Mithra
          </div>
          <div className="mt-1 text-lg sm:text-xl font-serif text-[#6B665E]">
            कथा मित्र
          </div>
          <div className="mt-2 text-xs uppercase tracking-[0.22em] text-[#C05621]">
            Story Friend
          </div>
        </div>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-serif tracking-tight leading-[1.1] text-[#2D2A26] mb-5">
          Stories that meet you where you are.
        </h1>
        <p className="text-lg md:text-xl leading-relaxed text-[#6B665E] max-w-2xl mx-auto">
          A searchable library of stories that elevate, ground, and
          awaken. Search by what you&rsquo;re feeling, not just by topic,
          and find a story that meets you there.
        </p>
      </header>

      <article
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20"
        data-testid="about-page"
      >
        {/* ----- 2. Why it exists -------------------------------------- */}
        <section className="mt-12" data-testid="about-why">
          <SectionEyebrow>Why it exists</SectionEyebrow>
          <SectionHeading>What we consume shapes who we become.</SectionHeading>
          <div className="space-y-5 text-[#2D2A26] leading-relaxed text-base md:text-lg">
            <p>
              The yogic tradition calls this <em>śravaṇa</em>, conscious
              listening. It is treated as the first step of inner
              transformation. We listen, we reflect, and slowly, something
              within us begins to shift.
            </p>
            <p>
              Most platforms today optimize listening for engagement. The
              container itself corrupts whatever content lives inside it:
              an inspiring story on a feed sits between a vacation reel
              and a political argument, and the environment drains it of
              weight. By the time the story reaches us, we are already
              restless.
            </p>
            <p>
              Katha Mithra inverts this. No infinite scroll. No trending
              noise. No content engineered to hold attention. Just stories
              held in an environment designed for reflection, where the act
              of searching and reading is itself a small practice.
            </p>
          </div>
        </section>

        {/* ----- 3. Founder note --------------------------------------- */}
        {/* The only first-person block on the page. When the team grows,
            this is the one block that changes.

            The notebook scan (public/vision_mission.jpg) is the
            load-bearing artifact here — quote-without-image would feel
            like marketing; image-without-quote would feel like a relic.
            Together they're proof. */}
        <section className="mt-16" data-testid="about-founder-note">
          <figure className="bg-[#FBF4EB] border-l-4 border-[#C05621] rounded-r-lg p-6 sm:p-8">
            {/* Slight rotation + shadow gives the scan a "paper on desk"
                feel without crossing into skeuomorphic kitsch. */}
            <div className="flex justify-center mb-6">
              <a
                href="/vision_mission.jpg"
                target="_blank"
                rel="noopener noreferrer"
                className="block transform -rotate-1 hover:rotate-0 transition-transform duration-300 max-w-md w-full"
                aria-label="Open the original notebook page at full size"
                data-testid="about-vision-mission-link"
              >
                <img
                  src="/vision_mission.jpg"
                  alt="Scanned notebook page dated 8 October 2023, titled Modern Day Karma Yogi · Sadhana Tracker. Lists the vision (to raise, elevate, and promote conscious living) and the problem statement (use the power of internet and data in a right way)."
                  className="w-full h-auto rounded-md shadow-lg border border-[#E6E2D6] bg-white"
                  loading="lazy"
                />
              </a>
            </div>
            <blockquote className="text-[#2D2A26] leading-relaxed text-base md:text-lg italic">
              <p>
                This page is from a notebook I kept in October 2023. At
                the top I wrote: &ldquo;Vision: To raise / elevate /
                promote conscious living.&rdquo; Lower down, in the
                problem statement, I wrote a line to myself: &ldquo;Use
                the power of internet and data in a right / positive
                way.&rdquo; Katha Mithra is that line, grown up. It is
                one of two pillars of the ModernDayKarmaYogi ecosystem.
                The other is Aaptha Mithra, which supports the inner
                practice. This one supports the conscious listening.
              </p>
            </blockquote>
            <figcaption className="mt-4 text-xs uppercase tracking-[0.18em] text-[#6B665E]">
              A note from the founder &middot; 8 October 2023
            </figcaption>
          </figure>
        </section>

        {/* ----- 4. Satsang at internet scale -------------------------- */}
        <section className="mt-20" data-testid="about-satsang">
          <SectionEyebrow>The thesis</SectionEyebrow>
          <SectionHeading>Satsang at internet scale.</SectionHeading>
          <div className="space-y-5 text-[#2D2A26] leading-relaxed text-base md:text-lg">
            <p>
              In the Vedantic tradition, <em>satsang</em> is the company
              of the wise. Being around people whose lives point toward
              something true. It has always been local, intimate, slow.
            </p>
            <p>
              Katha Mithra is an attempt at satsang at internet scale,
              indexed by inner state.
            </p>
            <p>
              Aaptha Mithra serves the inner practice: <em>sadhana</em>{' '}
              for the individual. Katha Mithra serves the collective
              wisdom: satsang for the seeker. Together they form a quiet
              flywheel.
            </p>
            <p className="text-[#6B665E]">
              People practice. People transform. They share their story.
              Someone else, looking for a way through, finds it. They
              begin their own practice. The cycle deepens.
            </p>
          </div>
        </section>

        {/* ----- 5. Search by inner state ------------------------------ */}
        <section className="mt-20" data-testid="about-search">
          <SectionEyebrow>How it works</SectionEyebrow>
          <SectionHeading>Search by inner state.</SectionHeading>
          <div className="space-y-5 text-[#2D2A26] leading-relaxed text-base md:text-lg">
            <p>
              Katha Mithra is organized around the human condition, not
              just the human library. You can search from where you are:
            </p>
            <ul className="space-y-2 pl-1">
              {INNER_STATE_PROMPTS.map((line) => (
                <li
                  key={line}
                  className="flex gap-3 text-[#2D2A26] italic"
                >
                  <span className="text-[#C05621] not-italic shrink-0">&bull;</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <p>
              Behind the scenes, each story is enriched with
              inner-dimension tags drawn from yogic frameworks: life areas
              from the twelve-house <em>Puruṣārtha</em> architecture,
              inner obstacles from the <em>kleshas</em> and{' '}
              <em>antarāyas</em>, and the qualities that move a person
              through them from <em>pratipakṣa bhāvanā</em>. Thirty-five
              tags in total.
            </p>
            <p>
              You never have to know any of this to use it. You type what
              you feel. The system finds the resonance.
            </p>
          </div>
        </section>

        {/* ----- 6. Four kinds of stories ------------------------------ */}
        <section className="mt-20" data-testid="about-kinds">
          <SectionEyebrow>What lives here</SectionEyebrow>
          <SectionHeading>Four kinds of stories.</SectionHeading>
          <p className="text-[#2D2A26] leading-relaxed text-base md:text-lg mb-8">
            Personal Transformation Stories are live today. The other three
            are part of the same larger vision and will unfold over time.
          </p>
          <div className="space-y-5">
            {STORY_KINDS.map((kind) => (
              <div
                key={kind.title}
                className="bg-white border border-[#E6E2D6] rounded-xl p-5 sm:p-6"
                data-testid={`about-kind-${kind.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
                  <h3 className="text-xl md:text-2xl font-serif text-[#2D2A26]">
                    {kind.title}
                  </h3>
                  {kind.status && (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#C05621] bg-[#FBF4EB] border border-[#C05621]/30 rounded-full px-2.5 py-0.5">
                      {kind.status}
                    </span>
                  )}
                </div>
                <p className="text-[#2D2A26] leading-relaxed">{kind.body}</p>
                {kind.takeaway && (
                  <p className="mt-3 text-sm md:text-base text-[#6B665E] italic">
                    &ldquo;{kind.takeaway}&rdquo;
                  </p>
                )}
              </div>
            ))}
          </div>
          <p className="mt-8 text-[#6B665E] leading-relaxed text-base md:text-lg italic">
            These are not four products. They are four faces of one offering.
          </p>
        </section>

        {/* ----- 7. Inner dimensions, made visible --------------------- */}
        <section className="mt-20" data-testid="about-dimensions">
          <SectionEyebrow>Inner dimensions</SectionEyebrow>
          <SectionHeading>Made visible, kept in the background.</SectionHeading>
          <div className="space-y-5 text-[#2D2A26] leading-relaxed text-base md:text-lg">
            <p>
              When a story is submitted, the system suggests its inner
              dimensions automatically. The creator reviews them, adjusts
              what doesn&rsquo;t fit, and publishes.
            </p>
            <p>
              When you read a story, those dimensions are visible on the
              page. Not as a quiz, but as a mirror. A reader may notice:{' '}
              <em>
                this person struggled with the same resistance I feel. The
                shift here is from fear to trust. This is not just a health
                story. It is also a story of courage.
              </em>
            </p>
            <p>
              The tags are an invisible matching layer for searchers, and a
              quiet reflective layer for readers. The human being stays at
              the center.
            </p>
            <p>
              If a term is unfamiliar, the{' '}
              <button
                type="button"
                onClick={() => navigate('/learn')}
                className="text-[#C05621] hover:text-[#A0461A] font-medium hover:underline underline-offset-2"
                data-testid="about-learn-link"
              >
                Learn section
              </button>{' '}
              explains the inner vocabulary the platform uses: what{' '}
              <em>fear</em>, <em>resistance</em>, <em>discipline</em>,{' '}
              <em>acceptance</em>, <em>clarity</em>, <em>devotion</em>, and{' '}
              <em>inner freedom</em> mean in the yogic sense, and how the
              tag system is structured. The goal is not philosophy for its
              own sake. It is to make subtle inner movements easier to
              notice.
            </p>
          </div>
        </section>

        {/* ----- 8. Ecosystem ------------------------------------------ */}
        <section className="mt-20" data-testid="about-ecosystem">
          <SectionEyebrow>The ecosystem</SectionEyebrow>
          <SectionHeading>Practice and listening.</SectionHeading>
          <p className="text-[#2D2A26] leading-relaxed text-base md:text-lg mb-8">
            Katha Mithra is one pillar of <strong>ModernDayKarmaYogi</strong>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E6E2D6] rounded-xl p-6">
              <div className="text-xs uppercase tracking-[0.18em] text-[#6B665E] mb-2">
                Aaptha Mithra
              </div>
              <h3 className="text-xl font-serif text-[#2D2A26] mb-2">
                Inner practice.
              </h3>
              <p className="text-[#2D2A26] leading-relaxed text-sm md:text-base">
                Sadhana, involvement, intention, mood, obstacles, daily
                conscious living.
              </p>
            </div>
            <div className="bg-white border border-[#C05621]/30 rounded-xl p-6">
              <div className="text-xs uppercase tracking-[0.18em] text-[#C05621] mb-2">
                Katha Mithra
              </div>
              <h3 className="text-xl font-serif text-[#2D2A26] mb-2">
                Conscious listening.
              </h3>
              <p className="text-[#2D2A26] leading-relaxed text-sm md:text-base">
                Stories, wisdom, and examples that inspire the next step.
              </p>
            </div>
          </div>
          <p className="mt-8 text-[#6B665E] leading-relaxed text-lg md:text-xl italic">
            Practice and listening. Together.
          </p>
        </section>

        {/* ----- 9. Closing manifesto ---------------------------------- */}
        <section className="mt-24 text-center" data-testid="about-closing">
          <BrandLogo
            className="h-16 w-16 mx-auto mb-8 opacity-90"
            alt=""
          />
          <p className="text-lg md:text-xl leading-relaxed text-[#2D2A26] max-w-2xl mx-auto mb-8">
            A young person opening Katha Mithra at 11 PM should not leave
            more anxious, more numb, or more envious of someone else&rsquo;s
            curated life. They should leave a little more grounded. A
            little more curious. A little more courageous. A little closer
            to their own dharma.
          </p>
          <p className="text-xl md:text-2xl font-serif text-[#2D2A26] mb-2">
            Not content for consumption.
          </p>
          <p className="text-xl md:text-2xl font-serif text-[#C05621]">
            Stories for transformation.
          </p>
        </section>

        {/* ----- CTA --------------------------------------------------- */}
        {/* "Lamp for someone walking a similar path" is the shared phrase
            with the landing page CTA. Edit both together so the two
            pages end on the same image. */}
        <div className="mt-20 pt-10 border-t border-[#E6E2D6] text-center">
          <p className="text-xl md:text-2xl font-serif leading-relaxed text-[#2D2A26] mb-4">
            Has a story shaped you?
          </p>
          <p className="text-[#6B665E] mb-8 max-w-xl mx-auto">
            Share your transformation story so it may become a lamp for someone walking a similar path.
          </p>
          <Button
            onClick={() => navigate('/submit')}
            className="rounded-full px-8 py-6 text-base font-medium bg-[#C05621] hover:bg-[#A0461A] text-white shadow-lg hover:shadow-xl btn-primary"
            data-testid="about-cta-submit"
          >
            <Feather className="h-5 w-5 mr-2" />
            Share your story
          </Button>
        </div>
      </article>
    </div>
  );
};

export default AboutPage;
